import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  Building,
  Calendar,
  Loader2
} from 'lucide-react';
import { formatDateTime } from '../utils/dateUtils';

interface InvoiceSuccessData {
  invoiceNumber: string;
  totalAmount: number;
  amountPaid: number;
  physicianName: string;
  clinicName: string;
  companyName: string;
  paidAt: string;
}

export const InvoicePaymentSuccess: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const [invoiceData, setInvoiceData] = useState<InvoiceSuccessData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentFailed, setPaymentFailed] = useState(false);

  // Check for payment status from Hyperswitch redirect
  const redirectStatus = searchParams.get('redirect_status');

  useEffect(() => {
    // Handle different redirect statuses from Hyperswitch
    if (redirectStatus === 'failed') {
      setPaymentFailed(true);
      setError('Payment failed. Please try again.');
      setLoading(false);
      return;
    }

    if (redirectStatus === 'requires_payment_method') {
      setPaymentFailed(true);
      setError('Payment could not be processed. Please try a different payment method.');
      setLoading(false);
      return;
    }

    // If succeeded or no status (assume success), fetch invoice details
    const loadInvoiceData = async () => {
      if (!token) {
        setError('Invalid payment reference');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`/api/invoice-payment/${token}/success`);
        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.error || 'Failed to load invoice information');
        }

        setInvoiceData(data.data);
      } catch (err) {
        // Even if we can't fetch details, the payment may have succeeded
        // Show a generic success message
        console.error('Failed to load invoice details:', err);
        setInvoiceData({
          invoiceNumber: 'N/A',
          totalAmount: 0,
          amountPaid: 0,
          physicianName: '',
          clinicName: '',
          companyName: '',
          paidAt: new Date().toISOString()
        });
      } finally {
        setLoading(false);
      }
    };

    loadInvoiceData();
  }, [token, redirectStatus]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading payment confirmation...</p>
        </div>
      </div>
    );
  }

  // Payment failed
  if (paymentFailed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Failed</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => window.history.back()}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Payment succeeded
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Payment Successful!</h1>
            <p className="text-gray-600">Your invoice payment has been processed</p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Success Message */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
          <div className="flex items-center space-x-3">
            <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
            <div>
              <h2 className="text-lg font-semibold text-green-900">Payment Confirmed</h2>
              <p className="text-green-800 mt-1">
                {invoiceData?.invoiceNumber !== 'N/A' ? (
                  <>Invoice {invoiceData?.invoiceNumber} has been paid successfully.</>
                ) : (
                  <>Your payment has been processed successfully.</>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Payment Details */}
        {invoiceData && invoiceData.invoiceNumber !== 'N/A' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Details</h2>

            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <FileText className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Invoice Number</p>
                  <p className="font-medium text-gray-900">{invoiceData.invoiceNumber}</p>
                </div>
              </div>

              {invoiceData.companyName && (
                <div className="flex items-center space-x-3">
                  <Building className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">From</p>
                    <p className="font-medium text-gray-900">{invoiceData.companyName}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center space-x-3">
                <Calendar className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Payment Date</p>
                  <p className="font-medium text-gray-900">
                    {formatDateTime(invoiceData.paidAt, { month: 'long' })}
                  </p>
                </div>
              </div>

              {invoiceData.amountPaid > 0 && (
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-900">Amount Paid</span>
                    <span className="text-2xl font-bold text-green-600">
                      ${invoiceData.amountPaid.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-900">What happens next?</h3>
              <ul className="mt-2 space-y-1 text-sm text-blue-800">
                <li>Your payment confirmation will be sent to your email</li>
                <li>The invoice status will be updated in your account</li>
                <li>You can close this window</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            Thank you for your payment. You may now close this window.
          </p>
        </div>
      </div>
    </div>
  );
};
